import type { PropsWithChildren, SyntheticEvent } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import Box from "@mui/material/Box";
import { DateTimeFormatRepository } from "consts/general";
import { getFormInputProps } from "util/forms";
import { selectPSPs, selectSetupActiveCompany } from "features/general/selectors";
import Autocomplete from "ui/atoms/Autocomplete";
import { FileInput } from "ui/forms/formik-elements";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, type UploadSettlementProps } from "./types";

const UploadSettlement = ({
    values,
    isBootstrapDataLoading,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    bootstrapData,
    children,
    ...form
}: PropsWithChildren<UploadSettlementProps>) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values);
            setTimeout(() => setFieldTouched(formField, true, true));
        };

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            sx={{
                pt: 2,
                display: 'grid',
                gap: 2
            }}
        >
            <LocalizationProvider
                dateAdapter={AdapterMoment}
            >
                <DatePicker
                    disableFuture
                    label='Settlement Date'
                    format={DateTimeFormatRepository.Default}
                    slotProps={{
                        textField: {
                            ...getFormInputProps(FormField.SettlementDate, form),
                            name: FormField.SettlementDate,
                            size: 'small',
                            required: true,
                            fullWidth: true,
                            onBlur: handleBlur
                        }
                    }}
                    value={values[FormField.SettlementDate]}
                    onChange={value => {
                        setFieldValue(FormField.SettlementDate, value);
                        setTimeout(() => setFieldTouched(FormField.SettlementDate, true, true));
                    }}
                />
            </LocalizationProvider>
            <StatefulSelector
                fullWidth
                disableClearable
                loading={isBootstrapDataLoading}
                layout={null}
                selectOptions={selectSetupActiveCompany}
                value={values[FormField.Mor]}
                onChange={onAutocompleteChange(FormField.Mor)}
                TextFieldProps={{
                    ...getFormInputProps(FormField.Mor, form),
                    required: true,
                    fullWidth: true,
                    name: FormField.Mor,
                    onBlur: handleBlur,
                    label: 'MOR (Merchant of Record)'
                }}
            />
            <StatefulSelector
                fullWidth
                disableClearable
                loading={isBootstrapDataLoading}
                layout={null}
                selectOptions={selectPSPs}
                value={values[FormField.Psp]}
                onChange={onAutocompleteChange(FormField.Psp)}
                TextFieldProps={{
                    ...getFormInputProps(FormField.Psp, form),
                    required: true,
                    fullWidth: true,
                    name: FormField.Psp,
                    onBlur: handleBlur,
                    label: 'PSP (Payment Service Provider)'
                }}
            />
            <Autocomplete
                fullWidth
                disablePortal
                disableClearable
                layout={null}
                loading={isBootstrapDataLoading}
                options={bootstrapData[FormField.CsvSeparator]}
                value={values[FormField.CsvSeparator]}
                onChange={onAutocompleteChange(FormField.CsvSeparator)}
                TextFieldProps={{
                    ...getFormInputProps(FormField.CsvSeparator, form),
                    required: true,
                    fullWidth: true,
                    name: FormField.CsvSeparator,
                    onBlur: handleBlur,
                    label: 'CSV Separator'
                }}
            />
            <FileInput
                multiple={false}
                name={FormField.File}
                file={values[FormField.File]}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                placeholderProps={{
                    sx: {
                        opacity: 1
                    }
                }}
            />
            {children}
        </Box>
    );
};

export default UploadSettlement;
